import { CALL_HISTORY_METHOD } from 'connected-react-router';
import { NavigationActions } from '@makemydeal/dr-activities-common';
import { tradeInHistory } from './tradeInHistory';
import { getPathname, getTargetPath, isTradeInRoute } from '../utils/middlewareHelpers';

/**
 * Intercepts trade-in step navigations and applies them to the trade-in in-memory
 * history instead of the shared browser history.
 *
 * Two navigation mechanisms are handled:
 *  - connected-react-router push/replace/etc. (CALL_HISTORY_METHOD)
 *  - sales-checkin navigation infra (NAVIGATE_TO), which navigationMiddleware would
 *    otherwise turn into a browser push, changing the app route away from
 *    /showroom/trade-in and unmounting the showroom shell.
 *
 * This keeps the browser URL on /showroom/trade-in (so the showroom shell stays
 * mounted) while the trade-in step routing happens in its own decoupled space.
 *
 * IMPORTANT: this middleware MUST be registered BEFORE both routerMiddleware and
 * navigationMiddleware so their browser navigations never fire for trade-in routes.
 * Navigations to non trade-in routes pass through untouched.
 */
export const tradeInRouterMiddleware =
    () =>
    (next: (action: unknown) => unknown) =>
    (action: { type?: string; payload?: string | { method?: string; args?: unknown[] } }) => {
        if (action?.type === CALL_HISTORY_METHOD) {
            const payload = action.payload as { method?: string; args?: unknown[] } | undefined;
            const { method, args = [] } = payload || {};
            const path = getTargetPath(args);
            const historyMethod = method ? (tradeInHistory as unknown as Record<string, unknown>)[method] : undefined;

            if (isTradeInRoute(path) && typeof historyMethod === 'function') {
                (historyMethod as (...a: unknown[]) => void).apply(tradeInHistory, args);
                return undefined; // swallow so the browser router stays on /showroom/trade-in
            }
        }

        if (action?.type === NavigationActions.NAVIGATE_TO) {
            const route = action.payload as string;

            if (isTradeInRoute(route)) {
                tradeInHistory.push(getPathname(route) as string);
                return undefined; // swallow so navigationMiddleware never pushes the browser router
            }
        }

        return next(action);
    };
